import { take } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { DxTextBoxComponent } from 'devextreme-angular';
import { filter } from 'rxjs/operators';
import { hasRequiredField } from '../../../app.functions';

import { Subject, BehaviorSubject, Subscription, Observable } from 'rxjs';

import { StateModeEnum } from '../../../app.enums';
import { AppHelper } from '../../../app.helper';
import { AppSettings } from '../../../app.settings';
import { HandleErrorModel } from '../../../core/models/handle-error.model';
import { MessageService } from '../../../shared-services/message/message.service';

@Component({
    selector: 'gestio-textbox-generate-product-code',
    templateUrl: './gestio-textbox-generate-product-code.component.html',
    styleUrls: ['./gestio-textbox-generate-product-code.component.scss'],
})
export class GestioTextboxGenerateProductCodeComponent implements OnInit {
    @Input() label: string = null;
    @Input() changeLabel: boolean = true;

    @Input() width: string = '100%';
    @Input() height: string = '100%';
    @Input() widthHelp: string = '300px';

    @Input() help: string = null;
    @Input() copyButton: boolean = true;
    @Input() control: FormControl = new FormControl(null, []);
    @Input() placeholder: string = '';

    @Input() mask: string = null;
    @Input() useMaskedValue: boolean = true;
    @Input() showClearButton: boolean = true;
    @Input() upper: boolean = false;

    @Input() validationMessageMode: string = 'always';
    @Input() errorRequired: string = `Deve ser informado.`;
    @Input() errorInvalid: string = `Valor inválido.`;
    @Input() maskInvalidMessage: string = `Valor inválido.`;

    @Input() focus$: Subject<boolean> = new Subject<boolean>();
    @Input() state$: BehaviorSubject<StateModeEnum> =
        new BehaviorSubject<StateModeEnum>(StateModeEnum.undefined);

    @Output() onValidMask = new EventEmitter<any>();
    @Output() onClearClick = new EventEmitter<any>();
    @Output() onEnterKeyEvent = new EventEmitter<any>();

    @ViewChild(DxTextBoxComponent) textBox: DxTextBoxComponent;

    visible: boolean = false;

    popoverVisible: boolean = false;
    upperCaseValue: string = null;

    private focusSubscription: Subscription;
    private _stateSubscription: Subscription;

    generateCodeButton;

    constructor(private _http: HttpClient, private _message: MessageService) {
        this.generateCodeButton = {
            template:
                '<i class="far fa-refresh" style="color: #798AA3; width: 18px; height: 18px; margin-top: 4px"></i>',
            type: 'normal',
            onClick: () => this.actionGerarCodigoInterno(),
        };
    }

    ngOnInit() {
        this.focusSubscription = this.focus$
            .pipe(filter((b: boolean) => b == true))
            .subscribe(() => {
                setTimeout(() => {
                    if (this.textBox && this.textBox.instance) {
                        this.textBox.instance.focus();
                    }
                }, 0);
            });

        if (this.upper) {
            this.control.valueChanges.subscribe(() => {
                this.upperCaseValue = this.control.value;
                AppHelper.setCtrlValue(
                    this.control,
                    this.control.value.toUpperCase()
                );
            });
        }

        this._stateSubscription = this.state$.subscribe((r) => {
            this.visible = r == StateModeEnum.insert || r == StateModeEnum.edit;
        });
    }

    ngOnDestroy() {
        this.focusSubscription.unsubscribe();
        this._stateSubscription.unsubscribe();
    }

    ngOnChanges() {}

    get _caption(): string {
        return this.label.replace(/\*/g, ' ');
    }

    get _captionLabel(): string {
        if (!this.changeLabel) {
            return this.label;
        }

        if (this.control && hasRequiredField(this.control)) {
            let t = this.label.replace(/\*/g, ' ');
            return `${t} *`;
        }
        return this.label;
    }

    get validationStatus() {
        return this.control.invalid && this.control.touched
            ? 'invalid'
            : 'valid';
    }

    isValid(): boolean {
        if (!this.control) {
            return false;
        }

        if (this.control.pristine && this.control.untouched) {
            return true;
        }

        return !this.control.invalid;
    }

    validationError(): any {
        if (!this.control) {
            return { message: null };
        }

        if (!this.control.errors) {
            return { message: null };
        } else {
            // console.log("CONTROL ERROR ->", this.control.errors, this.control.valid);
            if (this.control.errors.required) {
                return { message: this.errorRequired };
            } else if (this.control.errors.minlength) {
                return {
                    message: `Deve possuir no mínimo ${this.control.errors.minlength.requiredLength} caracteres.`,
                };
            } else if (this.control.errors.maxlength) {
                return {
                    message: `Deve possuir no máximo ${this.control.errors.maxlength.requiredLength} caracteres.`,
                };
            } else if (this.control.errors.invalidMask) {
                return { message: this.maskInvalidMessage };
            } else {
                if (this.errorInvalid) {
                    return { message: this.errorInvalid };
                } else {
                    return { message: `Inválido.` };
                }
            }
        }
    }

    private _getOnlyValue(v: string) {
        let r = v.trim();
        return r.replace(/\-/g, '');
    }

    actionOnEnterKey(e: any) {
        this.onEnterKeyEvent.emit(this.control.value);
    }

    actionValueChanged(e: any) {
        if (e && e.value && this.mask && this.textBox) {
            if (this.textBox.isValid) {
                this.onValidMask.next(e.value);
            } else {
                let v = this._getOnlyValue(e.value);
                if (v == null || v.length == 0) {
                    this.textBox.isValid = true;
                } else this.control.setErrors({ invalidMask: true });
            }
        }
    }

    actionContentReady(e: any) {
        const selector = e.element.querySelector('.dx-icon-clear');
        if (selector) {
            selector.addEventListener('click', () => this.onClearClick.emit());
        }
    }

    togglePopover() {
        this.popoverVisible = !this.popoverVisible;
    }

    actionGerarCodigoInterno() {
        this._gerarCodigoInterno()
            .pipe(take(1))
            .subscribe(
                (r) => {
                    this._setCodigoInterno(r);
                },
                (e) => {
                    let err = new HandleErrorModel(e);
                    this._message.errorMessageExpress(err);
                }
            );
    }

    private _gerarCodigoInterno(): Observable<any> {
        return this._http.post<any>(
            `${AppSettings.API_ENDPOINT_CADASTROS}/cadastro/produto/gerarcodigointerno`,
            null
        );
    }

    private _setCodigoInterno(codint: string): void {
        if (AppHelper.isInsertEditMode(this.state$.value)) {
            AppHelper.setCtrlValue(this.control, codint);
            this.control.updateValueAndValidity();
        }
    }
}
